import { FC, useCallback, useEffect, useState } from 'react'
import Big from 'big.js'
import { Tooltip } from 'react-tooltip'
import { Box } from '../../../shared_components/UI/Box'
import { useSelector } from 'react-redux'
import { periods } from '../../../utils/dateManipulation'
import { BarWrapper, BoxSubHeading } from '../parts'
import { amountFromWei } from '../../../utils/amount'

import { IPool, TGEPriceRange } from '../../../types/reducerTypes'

import ProgressBar from '../../Home/parts/progress-bar'
import {
  selectAccount,
  selectIsEthRaise,
} from '../../../modules/web3/selectors'
import useWeb3 from '../../../hooks/useWeb3'
import useCountdownTimer from '../../../hooks/useCountdownTimer'
import Open from './Open'
import DilutedButton from './DilutedButton'
import ClaimEstimates from './ClaimEstimates'
import { IStakes } from '../../../hooks/useMasterContract'
import { getPeriod } from '../../../utils/getPeriod'
import { numberWithCommas } from '../../../utils/commaSeperators'
import { USDB_DECIMALS } from '../../../config'
import styled from 'styled-components'
import useNetworkId from '../../../hooks/useNetworkId'
import { useHistory, useRouteMatch } from 'react-router-dom'
import {
  selectProjectHasTasks,
  selectTotalAllocation,
} from '../../../modules/missionControl/selectors'
import { ThemedButton } from '../../../shared_components/ThemedButton'
import AnimatedLoader from '../../../shared_components/AnimatedLoader'
import usePoolRaisePercentages from './usePoolRaisePercentages'
import { showDecimalsIfNeeded } from '../../Dashboard/Allocations/InvestmentInfo'
import { selectUser } from '../../../modules/user/selectors'
import { KYCStatus } from '../../../modules/user/slice'
import { ethers } from 'ethers'

interface Props {
  pool: IPool
  calculatedMaxAllocation: number | null
  calculatedTaxFreeAllocation: number
  whitelistAllocation: number
}

const Swap: FC<Props> = ({
  pool,
  calculatedMaxAllocation,
  calculatedTaxFreeAllocation,
  whitelistAllocation,
}) => {
  const account = useSelector(selectAccount)
  const user = useSelector(selectUser)

  const isLabsPool = pool?.isVetted

  const isKycPool = pool?.poolSettings?.requiresKYC
  const kycStatus = user?.kycStatus
  const kycComplete = kycStatus === KYCStatus.APPROVED_IN_CONTRACT
  const baselineAllocation = pool?.poolSettings?.baselineAllocation

  const {
    blastProvider,
    contracts: { master },
  } = useWeb3()

  const isPoolValid = blastProvider && ethers.isAddress(pool?.poolAddress)

  const isEthRaise = useSelector(selectIsEthRaise)
  const [period, setPeriod] = useState('')
  const [stakes, setStakes] = useState<IStakes>()

  const [maxAllocation, setMaxAllocation] = useState(
    calculatedMaxAllocation ?? 0,
  )
  const [price, setPrice] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)

  const [remainingAllocation, setRemainingAllocation] = useState(
    maxAllocation
      ? maxAllocation.toString()
      : !account
      ? String(baselineAllocation)
      : '0',
  )

  const totalAllocation = useSelector(selectTotalAllocation)

  const { wrongNetwork } = useNetworkId()

  const timeLeft = useCountdownTimer(pool?.privateSale?.end)
  const timeToOpen = useCountdownTimer(pool?.privateSale?.start)

  const projectHasTasks = useSelector(selectProjectHasTasks)

  const isTbc = pool?.isTbc

  const poolIsClosed =
    pool?.privateSale?.end &&
    new Date(pool?.privateSale?.end).getTime() - new Date().getTime() <
      // 5 * 1000 * 60 // five minutes
      1 * 1000 * 60 // 1 minute

  const totalSupplyForSale = pool?.poolSettings?.totalSupplyForSale ?? 0
  const poolPrice = pool?.privateSale?.price ?? '0'

  const { percentSoftCap, isDiluted } = usePoolRaisePercentages({
    poolAddress: pool?.poolAddress,
    totalSupplyForSale,
    price: poolPrice,
  })
  const percentage = percentSoftCap
  const percentageString = String(percentage)
  const raiseCurrency = pool?.poolSettings?.raiseCurrency ?? 'usdb'

  const fetchStakes = useCallback(async () => {
    try {
      if (pool.isLegacyPool) return
      const stakes = await master.stakes(pool?.poolAddress)
      setStakes(stakes)
    } catch (e) {
      console.error(e)
    }
  }, [pool?.poolAddress, pool?.isLegacyPool, master])

  const calculateRemaining = useCallback(() => {
    if (calculatedMaxAllocation === null) return
    if (!maxAllocation) return
    const currentInvestment = stakes?.amount

    if (!currentInvestment) return

    const inBUSD = isLabsPool
      ? new Big(currentInvestment)
      : amountFromWei(currentInvestment, USDB_DECIMALS)

    setRemainingAllocation(() => {
      const withDeduction =
        maxAllocation -
        (isEthRaise ? Number(inBUSD.toFixed(5)) : parseInt(inBUSD.toString()))
      return Math.abs(withDeduction).toString()
    })
  }, [calculatedMaxAllocation, maxAllocation, stakes, isEthRaise, isLabsPool])

  useEffect(() => {
    if (!calculatedMaxAllocation) return
    calculateRemaining()
  }, [
    calculatedMaxAllocation,
    calculatedTaxFreeAllocation,
    whitelistAllocation,
    calculateRemaining,
  ])

  const getAndSetPeriod = useCallback(() => {
    setPeriod(
      getPeriod(
        !!pool?.isDeployed,
        pool?.privateSale?.start,
        pool?.privateSale?.end,
      ),
    )
  }, [pool?.isDeployed, pool?.privateSale?.end, pool?.privateSale?.start])

  const calculateAndSetPrice = useCallback(() => {
    setPrice(
      !!pool.publicSale?.price && !new Big(pool.publicSale?.price || 0)?.eq(0)
        ? `1 ${raiseCurrency.toUpperCase()} = ${new Big(1)
            .div(pool.privateSale?.price ?? 0)
            .toFixed(2)} ${pool?.ticker}`
        : null,
    )
  }, [pool, raiseCurrency])

  useEffect(() => {
    if (!price) calculateAndSetPrice()
    if (!isPoolValid) return
    if (!!account && !wrongNetwork) fetchStakes()
  }, [
    fetchStakes,
    price,
    account,
    getAndSetPeriod,
    calculateAndSetPrice,
    isPoolValid,
    wrongNetwork,
  ])

  useEffect(() => {
    setMaxAllocation(calculatedMaxAllocation)
  }, [calculatedMaxAllocation, maxAllocation])

  useEffect(() => {
    setPeriod(
      getPeriod(
        !!pool?.isDeployed,
        pool?.privateSale?.start,
        pool?.privateSale?.end,
      ),
    )
  }, [timeLeft, timeToOpen, pool])

  const softCap = `${showDecimalsIfNeeded(
    Number(Number(totalSupplyForSale) * Number(poolPrice)),
    isEthRaise ? 5 : 2,
  )} ${raiseCurrency.toUpperCase()}`

  const { url } = useRouteMatch()
  const history = useHistory()

  function navToMissions() {
    history.push(url + '/missions')
  }

  const maxOrRemainingText =
    maxAllocation &&
    remainingAllocation !== maxAllocation.toString() &&
    period === periods.upcoming
      ? 'Remaining Whitelist amount'
      : 'Max Whitelist amount'

  const hasAllocationDiscrepency =
    !!maxAllocation && maxAllocation !== totalAllocation

  const investingOpen =
    !isTbc && (period === periods.private || period === periods.public)

  return (
    <SwapBoxContainer isActiveLaunch={!isLabsPool && investingOpen}>
      <Box className="top-bar" ac jsb>
        <ExchangeRate pool={pool} />
        {!isTbc &&
          (!isKycPool || kycComplete ? (
            <DilutedButton isDiluted={isDiluted} isEthRaise={isEthRaise} />
          ) : (
            <></>
            // <KYC />
          ))}
      </Box>
      <Box className="inner" dc>
        <Box justify="space-between" align="flex-end">
          <BoxSubHeading>Fundraise goal</BoxSubHeading>
          <BoxSubHeading fullOpacity isValue>
            {isTbc ? 'TBD' : softCap}
          </BoxSubHeading>
        </Box>
        <Box ac>
          {(isTbc || period === periods.upcoming) && (
            <Box marginTop={16} justify="space-between" marginBottom={16}>
              <Box justify="space-between" ac g={8}>
                <Box align="center">
                  <BoxSubHeading>{maxOrRemainingText} </BoxSubHeading>
                  <img
                    src="/img/icons/diluted-info.svg"
                    style={{ marginLeft: 8 }}
                    alt="Max Whitelist amount info tooltip icon"
                    data-tooltip-id="max-allocation-tooltip"
                  />
                  <Tooltip
                    id="max-allocation-tooltip"
                    place="bottom-start"
                    opacity={1}
                    style={{
                      marginLeft: '8px',
                      maxWidth: '230px',
                      zIndex: 1,
                    }}
                  >
                    <span>
                      Please allow up to an hour for your additionally earned
                      allocation to appear on chain.
                    </span>
                  </Tooltip>
                </Box>
              </Box>
              {!isTbc && calculatedMaxAllocation === null ? (
                <Box ac jfe>
                  <AnimatedLoader onlyWhite size={14} />
                </Box>
              ) : maxAllocation === 0 ? (
                <BoxSubHeading>0 USDB</BoxSubHeading>
              ) : maxAllocation &&
                remainingAllocation !== maxAllocation.toString() ? (
                <BoxSubHeading fullOpacity>
                  {Number(remainingAllocation)}
                  <BoxSubHeading fullOpacity isValue>
                    /
                    {showDecimalsIfNeeded(
                      maxAllocation ?? 0,
                      isEthRaise ? 5 : 2,
                    )}{' '}
                    {raiseCurrency.toUpperCase()}
                  </BoxSubHeading>
                </BoxSubHeading>
              ) : (
                <BoxSubHeading fullOpacity isValue>
                  {isTbc
                    ? 'TBD'
                    : showDecimalsIfNeeded(
                        maxAllocation ?? 0,
                        isEthRaise ? 5 : 2,
                      )}{' '}
                  {!isTbc && raiseCurrency.toUpperCase()}
                </BoxSubHeading>
              )}
            </Box>
          )}
        </Box>
        {!isTbc && period !== periods.upcoming ? (
          <BarWrapper direction="column">
            <ProgressBar
              clearBg
              isDiluted={isDiluted}
              displayText={percentageString}
              percentage={
                percentage ? Math.min(Math.max(percentage, 0), 100) : 0
              }
            />
            {period !== 'completed' && (
              <Box dc g={0}>
                <Box marginTop={12} justify="space-between" ac g={4}>
                  <Box align="center" g={6}>
                    <BoxSubHeading>
                      {maxAllocation &&
                      remainingAllocation !== maxAllocation.toString()
                        ? 'Remaining Whitelist amount'
                        : 'Max Whitelist amount'}{' '}
                    </BoxSubHeading>
                  </Box>
                  {!pool?.poolAddress ? (
                    'TBD'
                  ) : calculatedMaxAllocation === null ? (
                    <Box ac jfe>
                      <AnimatedLoader onlyWhite size={14} />
                    </Box>
                  ) : maxAllocation === 0 ? (
                    <BoxSubHeading fullOpacity isValue>
                      {isTbc ? 'TBC' : 0} {raiseCurrency.toUpperCase()}
                    </BoxSubHeading>
                  ) : maxAllocation &&
                    remainingAllocation !== maxAllocation.toString() ? (
                    <BoxSubHeading fullOpacity isValue>
                      {Number(remainingAllocation)}
                      <BoxSubHeading fullOpacity isValue>
                        /{numberWithCommas(maxAllocation ?? 0)}{' '}
                        {raiseCurrency.toUpperCase()}
                      </BoxSubHeading>
                    </BoxSubHeading>
                  ) : (
                    <BoxSubHeading fullOpacity isValue>
                      {isTbc ? 'TBC' : numberWithCommas(maxAllocation ?? 0)}{' '}
                      {raiseCurrency.toUpperCase()}
                    </BoxSubHeading>
                  )}
                </Box>
                {hasAllocationDiscrepency && !!totalAllocation && (
                  <Box
                    marginTop={12}
                    justify="space-between"
                    ac
                    marginBottom={4}
                  >
                    <Box align="center" g={6}>
                      <BoxSubHeading>
                        Pending Additional Allocation
                      </BoxSubHeading>
                      <img
                        src="/img/icons/diluted-info.svg"
                        style={{ marginLeft: 8 }}
                        alt="Max Whitelist amount info tooltip icon"
                        data-tooltip-id="max-allocation-tooltip"
                      />
                      <Tooltip
                        id="max-allocation-tooltip"
                        place="bottom"
                        opacity={1}
                        style={{
                          marginLeft: '8px',
                          maxWidth: '230px',
                          zIndex: 1,
                        }}
                      >
                        <span>
                          Please allow up to an hour for your additionally
                          earned allocation to appear on chain.
                        </span>
                      </Tooltip>
                    </Box>
                    <BoxSubHeading fullOpacity isValue>
                      {totalAllocation} {raiseCurrency.toUpperCase()}
                    </BoxSubHeading>
                  </Box>
                )}
              </Box>
            )}
            {investingOpen && (
              <Open
                isLoading={isLoading}
                setIsLoading={setIsLoading}
                pool={pool}
                maxAllocation={maxAllocation}
                fetchStakes={fetchStakes}
                stakes={stakes}
                remainingAllocation={remainingAllocation}
              />
            )}

            <ClaimEstimates
              setIsLoading={setIsLoading}
              pool={pool}
              stakes={stakes}
              isComplete={period === 'completed'}
              fetchStakes={fetchStakes}
            />
          </BarWrapper>
        ) : isTbc ? (
          <Box dc g={16}>
            <p>Launch date and time will be announced soon</p>
            <a
              href="https://twitter.com/intent/user?screen_name=zaponchain"
              target="_blank"
              rel="noreferrer"
              style={{ width: '100%' }}
            >
              <ThemedButton $styleType="primary" w="100%">
                Follow us on X
              </ThemedButton>
            </a>
          </Box>
        ) : (
          <Box dc g={16}>
            <p>
              Pool opens in <strong>{timeToOpen}</strong>
            </p>
          </Box>
        )}
        {!isTbc && projectHasTasks && !poolIsClosed && (
          <Box marginTop={16}>
            <ThemedButton
              onClick={navToMissions}
              $styleType="primary"
              w="100%"
              p="0"
            >
              Increase Whitelist Amount
            </ThemedButton>
          </Box>
        )}
      </Box>
    </SwapBoxContainer>
  )
}

export default Swap

export const HollowButton = styled.button`
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.1);
  height: 20px;
  padding: 4px 10px;
  span.copy {
    color: ${({ theme }) => theme.colors.brandPrimary};
    font-feature-settings: 'ss01' on;
    font-family: 'Space Grotesk';
    font-size: 11px;
    font-style: normal;
    font-weight: 500;
    line-height: 12px; /* 109.091% */
    text-transform: uppercase;
  }
  .tooltip {
    margin-left: 4px !important;
    padding: 0;
    width: 16px;
  }
`

export const getExchangeRatePrice = ({
  ticker,
  price,
  poolSettings,
}: Pick<IPool, 'ticker' | 'poolSettings'> & { price: string }) => {
  const raiseCurrency = poolSettings?.raiseCurrency ?? 'usdb'

  if (!ticker || !price) return ''
  const calculatedPrice = 1 / Number(price.toString())

  if (Number.isNaN(calculatedPrice)) return ''
  const priceString = calculatedPrice.toFixed(0)

  return `1 ${raiseCurrency.toUpperCase()} = ${numberWithCommas(
    priceString,
  )} ${ticker}`
}

export const getExchangeRatePriceRange = ({
  ticker,
  priceRange,
  poolSettings,
}: Pick<IPool, 'ticker' | 'poolSettings'> & { priceRange: TGEPriceRange }) => {
  const raiseCurrency = poolSettings?.raiseCurrency ?? 'usdb'
  const isEthRaise = raiseCurrency === 'eth'

  if (!ticker || !priceRange || priceRange.length < 2) return ''
  const calculatedLowPrice = 1 / Number(priceRange[0].toString())
  const calculatedHighPrice = 1 / Number(priceRange[1].toString())

  if (Number.isNaN(calculatedLowPrice) || Number.isNaN(calculatedHighPrice))
    return ''
  const lowPriceString = calculatedLowPrice.toFixed(0)
  const highPriceString = calculatedHighPrice.toFixed(0)

  return `1 ${raiseCurrency.toUpperCase()} = ${numberWithCommas(
    lowPriceString,
  )}-${numberWithCommas(highPriceString)} ${ticker}`
}

const ExchangeRateContainer = styled.div`
  span {
    color: #030c12;
    font-size: 14px;
    font-style: normal;
    font-weight: 700;
    line-height: 22px; /* 157.143% */
  }
`
function ExchangeRate({ pool }: { pool: IPool }) {
  const { ticker, privateSale, poolSettings, isTbc } = pool
  return (
    <ExchangeRateContainer>
      <Box c>
        <span>
          {isTbc
            ? 'Price info coming soon'
            : getExchangeRatePrice({
                ticker,
                price: privateSale.price,
                poolSettings,
              })}
        </span>
      </Box>
    </ExchangeRateContainer>
  )
}

const SwapBoxContainer = styled(Box)<{ isActiveLaunch?: boolean }>`
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  background: rgba(255, 255, 255, 0.04);
  backdrop-filter: blur(12px);
  flex-direction: column;
  max-width: 312px;
  margin-top: ${({ isActiveLaunch }) => (isActiveLaunch ? -2 : 0)}rem;
  margin-bottom: ${({ isActiveLaunch }) => (!isActiveLaunch ? -1 : 0)}rem;
  .inner {
    padding: 1rem;
  }
  .top-bar {
    padding: 1rem;
    background: ${({ theme }) => theme.colors.brandPrimary};
    margin-left: -1px;
    margin-top: -1px;
    width: calc(100% + 2px);
    border-radius: 8px 8px 0 0;
  }
  @media screen and (max-width: 800px) {
    width: 100%;
    max-width: 100%;
  }
`
